import React, { useState, useEffect, useRef } from 'react';
import { userApiClient } from '../../api/HttpClient';
import { useUserContext } from '../../app/protectedRoute/UserContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Upload, FileText, X } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
  } from "../ui/dialog";

interface HostVerificationModalProps {
    open: boolean;
    onClose: () => void;
    onVerificationSuccess: () => void;
}

const HostVerificationModal: React.FC<HostVerificationModalProps> = ({ open, onClose, onVerificationSuccess }) => {
  const { state, dispatch } = useUserContext();
  const { hostID, organization_id } = state;
  const navigate = useNavigate();

  const [hostDocument, setHostDocument] = useState<File | null>(null);
  const [hostDocumentType, setHostDocumentType] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setHostDocument(event.target.files[0]);
    }
  };

  const handleHostVerificationSubmit = async () => {
    if (!hostDocument || !hostDocumentType) {
      alert('Please select a document and document type.');
      return;
    }

    const formData = new FormData();
    formData.append('document', hostDocument);
    formData.append('document_type', hostDocumentType);
    formData.append('host', String(hostID));

    try {
      const client = await userApiClient;
      const response = await client.post<{ organization_is_verified: boolean }>(
        '/api/users/organization-verifications/',
        formData,
      );

      if (response && response.organization_is_verified) {
        dispatch({ type: 'SET_ORGANIZATION_VERIFIED', payload: true });
      }

      onVerificationSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error submitting host verification:', error);
      const message =
        error?.message ||
        error?.data?.error ||
        error?.data?.detail ||
        'Failed to submit host verification document.';
      alert(`Failed to submit host verification: ${message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
            <DialogHeader>
                <DialogTitle className="text-xl font-bold">Host Verification</DialogTitle>
                <DialogDescription>
                    Upload documents to verify your host account.
                </DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Document Type</label>
                <Select value={hostDocumentType} onValueChange={setHostDocumentType}>
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="Select a document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AADHAAR">Aadhar Card</SelectItem>
                    <SelectItem value="PAN">PAN Card</SelectItem>
                    <SelectItem value="LICENSE">Driving License</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Upload Document</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                />
                <Button
                  variant="outline"
                  className="w-full border-dashed border-2 h-12"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {hostDocument ? hostDocument.name : "Choose File"}
                </Button>
                {hostDocument && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-lg p-2">
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate flex-1">{hostDocument.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-1 text-destructive hover:text-destructive"
                      onClick={() => {
                        setHostDocument(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="flex-row justify-end gap-2 pt-2">
                <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={onClose}
                >
                    CANCEL
                </Button>
                <Button
                    className="bg-primary hover:bg-primary/90"
                    disabled={!hostDocument || !hostDocumentType}
                    onClick={handleHostVerificationSubmit}
                >
                    SUBMIT
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
  );
};

export default HostVerificationModal;
